/**
 * <gamba-product-gallery> — la galería de la PDP de Gamba.
 *
 * HACE DOS COSAS Y NADA MÁS (decisión D-17 del plan de la fase 3):
 *
 *   1. deriva el thumb activo del scroll del visor, con un IntersectionObserver
 *      sobre los slides;
 *   2. lleva el visor a la foto cuando se clickea un thumb.
 *
 * EL SNAP LO HACE CSS. `scroll-snap-type: y mandatory` + `scroll-snap-align:
 * start` + `scroll-behavior: smooth` viven en la hoja de la sección. Este
 * archivo no toca `scrollTop` cuadro a cuadro, no anima nada y NO ESCUCHA
 * `wheel`.
 *
 * POR QUÉ NO SE PORTA EL SECUESTRO DE LA RUEDA. El boceto captura `wheel` con
 * `{ passive: false }`, hace `preventDefault()` y anima `el.scrollTop` con
 * requestAnimationFrame (620ms, easing cubic-in-out propio). Lo único que eso
 * compra sobre el snap declarativo es "un gesto = exactamente una foto", y
 * cuesta el mismo patrón que el §12 del spec descarta en `installSmoothScroll()`
 * (rompe el trackpad, la accesibilidad y el scroll-anchoring). Queda anotado
 * como C-8: es una decisión de gesto y se resuelve probando, no leyendo. Si se
 * reabre, se reabre con una decisión escrita — no reintroduciendo el handler.
 *
 * POR QUÉ IntersectionObserver Y NO `Math.round(scrollTop / clientHeight)`. La
 * fórmula del boceto sólo funciona mientras cada slide mida exactamente el alto
 * del visor. Hoy miden eso (`flex: 0 0 100%`), pero el día que un slide cambie
 * de alto —un video con controles, un modelo 3D, un `aspect-ratio` distinto—
 * la cuenta empieza a devolver el índice equivocado sin tirar ningún error. El
 * observer mira dónde están los slides de verdad. Es además lo que hace
 * `assets/media-gallery.js` de Horizon.
 *
 * LO QUE EL BOCETO MOBILE MUESTRA NO ES EL DISEÑO MOBILE. Ahí este JS no corre:
 * hay dos `componentDidUpdate` en la misma clase y el segundo sobreescribe al
 * primero, así que `watchPdpShots()` nunca se vuelve a llamar (`fase-3-a` §9
 * D-6). El scroll libre y el borde que sólo se mueve al clickear son el efecto
 * de ese override, no una decisión. Este componente corre en los dos
 * breakpoints, con el comportamiento de escritorio.
 *
 * EL ESTADO INICIAL LO PINTA EL SERVIDOR. La sección emite `aria-current="true"`
 * en el primer thumb, así que sin JS la galería se ve entera, scrollea (es un
 * `overflow-y: auto` nativo, con snap y con teclado) y muestra el primer thumb
 * como activo. Lo único que se pierde sin JS es que el borde siga al scroll y
 * que el click en un thumb mueva el visor.
 *
 * `aria-current` Y NO `aria-pressed`: el estado cambia al scrollear, sin que
 * nadie apriete nada, y un botón que se reporta "pressed" por un scroll ajeno
 * miente. El razonamiento largo está en el comentario de cabecera de
 * `sections/gamba-main-product.liquid`.
 *
 * Refs perezosas, sin cachear, igual que `gamba-rail.js` y
 * `gamba-ideas-selector.js`: si un `morphSection` reemplaza los hijos sin
 * desconectar el custom element, una ref cacheada queda apuntando a un nodo
 * desprendido.
 */

/**
 * Banda central del visor, en `rootMargin`. Recorta el root al 10% central del
 * alto: el slide que la cruza es el que se está viendo. Es la forma robusta de
 * preguntar "cuál está en el medio" sin depender de que los slides midan lo
 * mismo, y no necesita `threshold` porque alcanza con que interseque.
 */
const CENTER_BAND = '-45% 0px -45% 0px';

class GambaProductGallery extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Slides que ahora mismo cruzan la banda central. Normalmente es uno; pueden
   * ser dos mientras el snap se acomoda o si algún slide es más bajo que la
   * banda.
   * @type {Set<Element>}
   */
  #crossing = new Set();

  connectedCallback() {
    // Delegado en el host, que sobrevive a un morph de los hijos.
    this.addEventListener('click', this.#onClick);
    this.#observe();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#crossing.clear();
  }

  /**
   * Lleva el visor a una foto y marca su thumb.
   *
   * @param {number} index - Índice de la foto, base 0.
   */
  show(index) {
    const slide = this.#slideAt(index);
    if (!slide) return;

    const viewer = this.#viewer;
    if (!viewer) return;

    /*
     * `scrollTo` SIN `behavior`, a propósito: el default (`auto`) resuelve al
     * `scroll-behavior` computado del elemento, que la hoja de la sección pone
     * en `smooth` y apaga bajo `prefers-reduced-motion: reduce`. Así el click y
     * la rueda se mueven igual (el boceto tenía dos curvas distintas según de
     * dónde viniera el gesto, D-5) y la preferencia de movimiento se respeta en
     * un solo lugar, el CSS.
     *
     * El destino se calcula con rects en vez de `offsetTop` porque `offsetTop`
     * se mide contra el `offsetParent`, que acá es el host y no el visor.
     */
    const top =
      viewer.scrollTop + (slide.getBoundingClientRect().top - viewer.getBoundingClientRect().top);

    viewer.scrollTo({ top });

    // Feedback inmediato: el observer confirma lo mismo unos frames después,
    // pero el borde no tiene por qué esperar a que termine el scroll.
    this.#mark(index);
  }

  /** @returns {Element | null} */
  get #viewer() {
    return this.querySelector('[data-gallery-viewer]');
  }

  /**
   * @param {number} index
   * @returns {Element | null}
   */
  #slideAt(index) {
    if (!Number.isInteger(index) || index < 0) return null;

    return this.querySelector(`[data-gallery-slide="${index}"]`);
  }

  /** Engancha el observer sobre los slides, con el visor como root. */
  #observe() {
    const viewer = this.#viewer;
    if (!viewer) return;

    const slides = this.querySelectorAll('[data-gallery-slide]');
    // Con una sola foto no hay thumbs ni scroll: nada que observar.
    if (slides.length < 2) return;

    this.#observer = new IntersectionObserver(this.#onCross, {
      root: viewer,
      rootMargin: CENTER_BAND,
    });

    for (const slide of slides) this.#observer.observe(slide);
  }

  /** @type {IntersectionObserverCallback} */
  #onCross = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) this.#crossing.add(entry.target);
      else this.#crossing.delete(entry.target);
    }

    if (this.#crossing.size === 0) return;

    const viewer = this.#viewer;
    if (!viewer) return;

    /*
     * Empate: gana el slide cuyo centro esté más cerca del centro del visor.
     * Se lee layout, pero sólo cuando el conjunto de slides que cruzan la banda
     * cambia (un puñado de veces por gesto), no por frame. No se compara
     * `intersectionRatio` porque el ratio es relativo al tamaño de CADA
     * elemento: un slide chico gana siempre aunque esté saliendo.
     */
    const box = viewer.getBoundingClientRect();
    const middle = box.top + box.height / 2;

    let winner = null;
    let closest = Infinity;

    for (const slide of this.#crossing) {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - middle);

      if (distance < closest) {
        closest = distance;
        winner = slide;
      }
    }

    if (winner) this.#mark(this.#indexOf(winner));
  };

  /**
   * Deja `aria-current="true"` en un solo thumb y lo BORRA de los demás
   * (`aria-current="false"` es válido, pero "no está" es la forma canónica de
   * "no es el actual" y es lo que los lectores anuncian mejor).
   *
   * @param {number} index - Índice de la foto activa, base 0.
   */
  #mark(index) {
    if (!Number.isInteger(index) || index < 0) return;

    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const current = this.#indexOf(thumb) === index;

      // Se escribe sólo si cambió: un setAttribute con el mismo valor igual
      // invalida el estilo del subárbol.
      if (current) {
        if (thumb.getAttribute('aria-current') !== 'true') thumb.setAttribute('aria-current', 'true');
      } else if (thumb.hasAttribute('aria-current')) {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Lee el índice que el Liquid horneó en el atributo. Los dos atributos
   * (`data-gallery-slide` y `data-gallery-thumb`) llevan el mismo número para
   * la misma foto.
   *
   * @param {Element} element
   * @returns {number}
   */
  #indexOf(element) {
    const raw =
      element.getAttribute('data-gallery-thumb') ?? element.getAttribute('data-gallery-slide');

    return Number(raw);
  }

  #onClick = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const thumb = target.closest('[data-gallery-thumb]');
    // Que cada galería maneje sus propios thumbs: dos en la misma página son
    // posibles (una PDP con un quick-view, por ejemplo).
    if (!thumb || thumb.closest('gamba-product-gallery') !== this) return;

    this.show(this.#indexOf(thumb));
  };
}

if (!customElements.get('gamba-product-gallery')) {
  customElements.define('gamba-product-gallery', GambaProductGallery);
}
